import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { T } from "@/lib/testIds";
import { Shield, Users, Store, Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";

const ROLE_TABS = [
  { key: "admin",    label: "Admin",    icon: Shield, hint: "Full control, all modules" },
  { key: "team",     label: "Team",     icon: Users,  hint: "Ops, procurement, shipping" },
  { key: "reseller", label: "Reseller", icon: Store,  hint: "Your account, your orders" },
];

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState(() => localStorage.getItem("login_role") || "admin");

  useEffect(() => {
    if (user && user !== false) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    localStorage.setItem("login_role", role);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/");
  };

  const activeTab = ROLE_TABS.find((t) => t.key === role) || ROLE_TABS[0];
  const ActiveIcon = activeTab.icon;

  return (
    <div className="min-h-screen w-full flex bg-[#0b0f19] text-slate-100 overflow-hidden">
      {/* ────── Left brand panel ────── */}
      <div className="hidden lg:flex lg:w-[52%] relative flex-col justify-between p-12 overflow-hidden">
        {/* Ambient background layers */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-indigo-600/25 rounded-full blur-[140px]" />
          <div className="absolute top-1/3 -right-32 w-[520px] h-[520px] bg-emerald-500/15 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] bg-amber-400/10 rounded-full blur-[110px]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:32px_32px]" />
        </div>

        {/* Top: brand */}
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white text-slate-900 rounded-md flex items-center justify-center font-heading font-bold shadow-lg">IR</div>
          <div>
            <div className="font-heading text-[15px] font-bold tracking-tight leading-none">Importrend ERP</div>
            <div className="text-[10.5px] text-slate-400 font-mono mt-1">operations control room</div>
          </div>
        </div>

        {/* Middle: value prop */}
        <div className="max-w-lg">
          <div className="inline-flex items-center gap-1.5 h-6 px-2 border border-white/10 bg-white/5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-slate-300 mb-4 backdrop-blur">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            multi-marketplace ops
          </div>
          <h1 className="font-heading text-[42px] xl:text-[52px] font-bold leading-[1.05] tracking-tight">
            Every marketplace,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 via-white to-emerald-300">
              one control room.
            </span>
          </h1>
          <p className="mt-5 text-slate-300 text-[14px] leading-relaxed">
            Inventory · China procurement · shipping · claims · GST · teams. Laid out like a spreadsheet, wired like software.
          </p>

          {/* Feature strip */}
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              { n: "99", label: "fields per order" },
              { n: "4", label: "marketplaces" },
              { n: "EN↔中", label: "translate built-in" },
            ].map((s) => (
              <div key={s.label} className="border border-white/10 bg-white/5 backdrop-blur rounded-md p-3">
                <div className="font-mono text-lg font-bold text-white">{s.n}</div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className="text-[10.5px] text-slate-500 font-mono flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          all systems operational · v1.0
        </div>
      </div>

      {/* ────── Right form panel ────── */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50 text-slate-900">
        <div className="w-full max-w-[420px]">
          {/* Mobile brand */}
          <div className="lg:hidden mb-6 flex items-center gap-2.5 justify-center">
            <div className="w-8 h-8 bg-slate-900 text-white rounded-md flex items-center justify-center font-heading font-bold">IR</div>
            <span className="font-heading text-[15px] font-semibold tracking-tight">Importrend ERP</span>
          </div>

          {/* Role tabs */}
          <div className="grid grid-cols-3 gap-1 p-1 bg-slate-200/70 rounded-md mb-6" data-testid="login-role-tabs">
            {ROLE_TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setRole(key)}
                data-testid={`login-role-${key}`}
                className={`h-9 rounded-sm text-[12px] font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${
                  role === key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {/* Form card */}
          <form
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-lg p-6 shadow-[0_10px_40px_-15px_rgba(15,23,42,0.15)]"
            data-testid="login-form"
          >
            <div className="mb-5">
              <div className="inline-flex items-center gap-1.5 mb-2">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                  role === "admin" ? "bg-amber-100 text-amber-700"
                  : role === "team" ? "bg-indigo-100 text-indigo-700"
                  : "bg-emerald-100 text-emerald-700"
                }`}>
                  <ActiveIcon size={14} />
                </div>
                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">
                  {activeTab.label} sign-in
                </span>
              </div>
              <h2 className="font-heading text-[22px] font-bold tracking-tight text-slate-900 leading-tight">
                Welcome back.
              </h2>
              <p className="text-[12.5px] text-slate-500 mt-1">{activeTab.hint}</p>
            </div>

            {/* Email */}
            <label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">Email</label>
            <div className="relative mt-1 mb-3">
              <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid={T.loginEmail}
                className="w-full h-10 pl-8 pr-2 border border-slate-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>

            {/* Password */}
            <label className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">Password</label>
            <div className="relative mt-1 mb-4">
              <Lock size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid={T.loginPassword}
                className="w-full h-10 pl-8 pr-9 border border-slate-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPass ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                data-testid="login-toggle-password"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>

            {error && (
              <div data-testid={T.loginError} className="mb-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                <span className="w-1 h-1 rounded-full bg-red-500 mt-1.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              data-testid={T.loginSubmit}
              disabled={busy}
              className="w-full h-10 bg-slate-900 text-white text-[13px] font-semibold rounded-md hover:bg-slate-800 active:bg-black disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition-all group"
            >
              {busy ? (
                <>
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            <div className="mt-5 pt-4 border-t border-slate-100 text-[11px] text-slate-500 text-center">
              Need access? Ask your admin to create an account with the right team permissions.
            </div>
          </form>

          <div className="mt-6 text-[10.5px] text-slate-400 text-center font-mono">
            © Importrend ERP · secure session
          </div>
        </div>
      </div>
    </div>
  );
}
